/* eslint-disable prettier/prettier */
import React, { useEffect, useRef } from 'react';
import { Activity } from '../../types/schedule.types';
import { ACTIVITY_TYPE_MAP } from '../../constants/activitytypes.constant';
import { MONTHS_LONG, getDayLabel, toLocalTimeHM } from '../../utils/dateUtils';
import styles from '../../styles/Schedule/DayActivitiesModal.module.css';

/**
 * "Expanded Activities" dialog (Image 3): opened by the +N pill in a
 * month-grid cell, listing ALL activities for that day.
 */
interface DayActivitiesModalProps {
	date: Date | null;
	activities: Activity[];
	onClose: () => void;
	onActivityClick?: (activity: Activity) => void;
}

const DayActivitiesModal: React.FC<DayActivitiesModalProps> = ({
	date,
	activities,
	onClose,
	onActivityClick,
}) => {
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!date) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose();
		};
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, [date, onClose]);

	if (!date) return null;

	const heading = `${getDayLabel(date)}, ${date.getDate()} ${MONTHS_LONG[date.getMonth()]}`;

	return (
		<div
			className={styles.overlay}
			onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => {
				if (ref.current && !ref.current.contains(e.target as Node))
					onClose();
			}}
		>
			<div
				className={styles.modal}
				ref={ref}
				role="dialog"
				aria-modal="true"
				aria-label={`Activities on ${heading}`}
			>
				<div className={styles.header}>
					<h3 className={styles.heading}>{heading}</h3>
					<button
						className={styles.closeBtn}
						onClick={onClose}
						aria-label="Close"
					>
						×
					</button>
				</div>

				<div className={styles.list}>
					{activities.length === 0 && (
						<p className={styles.empty}>
							No activities scheduled for this day.
						</p>
					)}
					{activities.map(activity => {
						const config = activity.activitytype
							? ACTIVITY_TYPE_MAP[activity.activitytype]
							: undefined;
						const accent =
							activity.activitytypecolor ||
							config?.color ||
							'#2E9E6B';
						const bg = config?.bgColor || '#F6F6F6';

						return (
							<button
								key={activity.id}
								className={styles.activity}
								style={{
									background: bg,
									borderBottomColor: accent,
								}}
								onClick={() => onActivityClick?.(activity)}
							>
								<span className={styles.time}>
									{toLocalTimeHM(activity.startTime)}–
									{toLocalTimeHM(activity.endTime)}
								</span>
								<span className={styles.title}>
									{activity.title}
								</span>
							</button>
						);
					})}
				</div>
			</div>
		</div>
	);
};

export default DayActivitiesModal;
